import { useState, useEffect, useLayoutEffect, useCallback } from "react";
import { request } from "../utils/api";
import toast from "react-hot-toast";
import { getCachedTiming, prefetchTiming } from "../utils/attendanceTimingCache";

const getInitialTimingState = () => ({
  isClockedIn: false,
  isOnBreak: false,
  clockInTime: null,
  lastBreakInTime: null,
  todayTotalWorkTime: "00:00:00",
  todayTotalBreakTime: "00:00:00",
  breakTime: "00:00:00",
  liveTracking: "00:00:00",
  lastTimingFetchAt: null,
  /** Client clock when we applied the last API/cache snapshot — used for +1s deltas (avoids server/client skew). */
  lastClientSyncAtMs: null
});

const useAttendance = () => {
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Activity Log State
  const [activityLog, setActivityLog] = useState([]);

  // Timing State — hydrate from shared cache (module or sessionStorage) so timer isn't stuck
  const [state, setState] = useState(() => {
    const cached = getCachedTiming();
    if (!cached) return getInitialTimingState();
    const { result, lastTimingFetchAt, cachedAt } = cached;
    return {
      ...getInitialTimingState(),
      ...result,
      lastTimingFetchAt,
      lastClientSyncAtMs: cachedAt != null ? cachedAt : Date.now(),
      isClockedIn: result.isClockedIn,
      isOnBreak: result.isOnBreak
    };
  });

  const [timingSynced, setTimingSynced] = useState(!!getCachedTiming());

  // Helper to parse "HH:MM:SS" to seconds
  const parseDurationToSeconds = (durationStr) => {
    if (!durationStr) return 0;
    const [h, m, s] = durationStr.split(":").map(Number);
    return (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
  };

  // Use currentTime (1s tick + bumps on sync) so elapsed matches the UI clock — avoids Date.now() vs tick drift after refresh.
  const nowMs = currentTime.getTime();

  // Derived values: backend totals + elapsed since lastClientSyncAtMs (same client anchor for work and break — no server lastBreakInTime skew).
  const calculateTrackedTime = () => {
    const baseWork = parseDurationToSeconds(state.todayTotalWorkTime);
    if (!state.isClockedIn || state.isOnBreak) {
      return baseWork;
    }
    if (state.lastClientSyncAtMs != null) {
      return baseWork + Math.max(0, Math.floor((nowMs - state.lastClientSyncAtMs) / 1000));
    }
    if (state.clockInTime) {
      return Math.max(0, Math.floor((nowMs - new Date(state.clockInTime).getTime()) / 1000));
    }
    return baseWork;
  };

  const calculateTotalBreakTime = () => {
    const baseBreak = parseDurationToSeconds(state.todayTotalBreakTime);
    if (!state.isClockedIn || !state.isOnBreak) {
      return baseBreak;
    }
    if (state.lastClientSyncAtMs != null) {
      return baseBreak + Math.max(0, Math.floor((nowMs - state.lastClientSyncAtMs) / 1000));
    }
    return baseBreak;
  };

  const trackedTime = calculateTrackedTime();
  const totalBreakTime = calculateTotalBreakTime();

  // Format helpers
  const formatTime = (date) => {
    if (!date) return "--:--:--";
    return new Date(date).toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  // Fetch Current Timing — uses shared prefetch (deduplicates with Login/PrivateRoute)
  const fetchTiming = useCallback(async () => {
    // Stale-while-revalidate: show timer from cache/zeros immediately; refresh when API returns.
    setTimingSynced(true);
    try {
      const response = await prefetchTiming();
      if (response?.status === 1 && response.result) {
        const lastTimingFetchAt = response.time_stamp || new Date().toISOString();
        const lastClientSyncAtMs = Date.now();
        setState((prev) => ({
          ...prev,
          ...response.result,
          lastTimingFetchAt,
          lastClientSyncAtMs,
          isClockedIn: response.result.isClockedIn,
          isOnBreak: response.result.isOnBreak
        }));
        setCurrentTime(new Date());
      }
    } catch (err) {
      console.error("Failed to fetch timing:", err);
    }
  }, []);

  // Fetch Activity Log
  const fetchActivityLog = useCallback(async () => {
    try {
      const response = await request("/attendance/activity-log?page=1&limit=20");
      if (response.status === 1 && response.result && Array.isArray(response.result.activities)) {
        // Map API activities to UI format if needed
        const logs = response.result.activities.map(act => ({
          id: act.id,
          time: new Date(act.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          title: act.activityType === "ClockIn" ? "Clock In" :
            act.activityType === "ClockOut" ? "Clock Out" :
              act.activityType === "BreakIn" ? "Break In" :
                act.activityType === "BreakOut" ? "Break Out" : act.activityType,
          icon: act.activityType.includes("Break") ? "coffee" : "clock",
          timestamp: act.timestamp, // Keep original for sorting if needed
          notes: act.notes
        }));
        setActivityLog(logs);
      }
    } catch (err) {
      console.error("Failed to fetch activity log:", err);
    }
  }, []);

  // Initial Load
  useEffect(() => {
    fetchTiming();
    fetchActivityLog();
  }, [fetchTiming, fetchActivityLog]);

  // Align clock right after mount/hydration so first paint isn’t behind real time after refresh.
  useLayoutEffect(() => {
    setCurrentTime(new Date());
  }, []);

  // Timer Logic for Live Updates — tick immediately then every 1s
  useEffect(() => {
    const tick = () => setCurrentTime(new Date());
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  // Helper to get location
  const getCurrentLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: 0, longitude: 0 });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Geolocation error:", error);
          resolve({ latitude: 0, longitude: 0 });
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  // Actions


  const clockIn = async () => {
    setLoading(true);
    try {
      const { latitude, longitude } = await getCurrentLocation();
      const payload = {
        location: "Office Main Entrance",
        latitude,
        longitude,
        notes: "Web Clock In"
      };
      const response = await request("/attendance/clock-in", {
        method: "POST",
        body: JSON.stringify(payload)
      });

      if (response.status === 0) {
        throw new Error(response.error || response.message || "Clock in failed");
      }

      // Update local state immediately for UX
      setState(prev => ({
        ...prev,
        isClockedIn: true,
        isOnBreak: false,
        clockInTime: new Date().toISOString(),
        lastClientSyncAtMs: Date.now(),
        todayTotalWorkTime: prev.todayTotalWorkTime === "00:00:00" ? "00:00:00" : prev.todayTotalWorkTime
      }));

      await fetchTiming();
      await fetchActivityLog();
      toast.success("Clock in successful");
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const clockOut = async (reason = "") => {
    setLoading(true);
    try {
      const { latitude, longitude } = await getCurrentLocation();
      const payload = {
        location: "Office Main Entrance",
        latitude,
        longitude,
        notes: reason || "Web Clock Out"
      };
      const response = await request("/attendance/clock-out", {
        method: "POST",
        body: JSON.stringify(payload)
      });

      if (response.status === 0) {
        throw new Error(response.error || response.message || "Clock out failed");
      }

      // Update local state immediately for UX
      setState(prev => ({
        ...prev,
        isClockedIn: false,
        isOnBreak: false,
        todayTotalWorkTime: response.result?.totalWorkTime || prev.todayTotalWorkTime
      }));

      await fetchTiming();
      await fetchActivityLog();
      toast.success("Clock out successful");
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startBreak = async (reason) => {
    setLoading(true);
    try {
      const { latitude, longitude } = await getCurrentLocation();
      const payload = {
        location: "Break Room",
        latitude,
        longitude,
        notes: reason
      };
      const response = await request("/attendance/break-in", {
        method: "POST",
        body: JSON.stringify(payload)
      });

      if (response.status === 0) {
        throw new Error(response.error || response.message || "Break start failed");
      }

      // Update local state immediately for UX
      setState(prev => ({
        ...prev,
        isOnBreak: true,
        lastBreakInTime: new Date().toISOString(),
        lastClientSyncAtMs: Date.now()
      }));

      await fetchTiming();
      await fetchActivityLog();
      toast.success("Break started");
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const endBreak = async () => {
    setLoading(true);
    try {
      const { latitude, longitude } = await getCurrentLocation();
      const payload = {
        location: "Break Room",
        latitude,
        longitude,
        notes: "Back from break"
      };
      const response = await request("/attendance/break-out", {
        method: "POST",
        body: JSON.stringify(payload)
      });
      if (response.status === 0) {
        throw new Error(response.error || response.message || "Break end failed");
      }

      // Update local state immediately for UX
      setState(prev => ({
        ...prev,
        isOnBreak: false,
        todayTotalBreakTime: response.result?.totalBreakTime || prev.todayTotalBreakTime,
        todayTotalWorkTime: response.result?.totalWorkTime || prev.todayTotalWorkTime,
        lastClientSyncAtMs: Date.now()
      }));
      setCurrentTime(new Date());

      await fetchTiming();
      await fetchActivityLog();
      toast.success("Break ended");
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addWorkLog = (log) => {
    // Mock implementation or future API
    console.log("Add Work Log", log);
    // If there's an API, call it here. For now, keep as placeholder or update if API exists.
  };

  return {
    currentTime,
    consoleTime: state.liveTracking,
    formatTime,
    formatDuration,
    state,
    totalBreakTime,
    trackedTime,
    clockInHours: trackedTime + totalBreakTime,
    activityLog,
    clockIn,
    startBreak,
    endBreak,
    clockOut,
    addWorkLog,
    loading,
    error,
    timingSynced
  };
};

export default useAttendance;
